import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useLocale } from 'next-intl'

import { createInfiniteHook, createQueryHook } from '@/shared/api'
import api from '@/shared/api/client'
import { ModelsNews } from '@/shared/api/generated-api'

export const enum NewsQueryKeys {
  NEWS_RECOMMENDED = 'news_recommended',
  NEWS_LIST = 'news_list',
  NEWS_DETAIL = 'news_detail',
  NEWS_MAIN_PAGE = 'news_main_page',
  NEWS_FAVORITE = 'news_favorite',
  NEWS_TAGS = 'news_tags'
}

export const DEFAULT_NEWS_LIMIT = 6

const getNewsRecommended = async () => {
  const response = await api.v2NewsRecommendedList()
  return response.recommended || []
}

const getNewsList = async ({
  offset,
  limit,
  tag
}: {
  offset: number
  limit?: number
  tag?: string
}) => {
  const response = await api.v2NewsList({
    limit: limit || DEFAULT_NEWS_LIMIT,
    offset: Number(offset || 0),
    ...(tag ? { tag } : {})
  })

  return {
    news: response.news || [],
    offset: offset || 0,
    limit: limit || DEFAULT_NEWS_LIMIT,
    total: response.total || 0
  }
}

// Labels are in the request language; filters still take the tag. A gateway
// without labels yet gets the tag as its own label.
const getNewsTags = async (): Promise<{ tag: string; label: string }[]> => {
  const response = await api.v2NewsTagsList()
  if (response.items)
    return response.items.map(({ tag = '', label }) => ({
      tag,
      label: label || tag
    }))
  return (response.tags || []).map(tag => ({ tag, label: tag }))
}

const getNewsDetail = async ({ id }: { id: string }) => {
  const response = await api.v2NewsDetail(id)
  return response.news
}

const getNewsMainPage = async ({
  limit,
  offset
}: {
  limit?: number
  offset?: number
}) => {
  // The gateway rejects the request with "invalid offset" when the parameter is
  // missing, despite the schema defaulting it to 0.
  const response = await api.v2NewsMainPageList({ limit, offset: offset ?? 0 })
  return response.main_pages || []
}

const getNewsFavorite = async () => {
  const response = await api.v2NewsFavoriteList()
  return response.favorite
}

const useNewsRecommendedFactory = createQueryHook<
  Record<string, never>,
  Awaited<ReturnType<typeof getNewsRecommended>>
>({
  getQueryKey: () => [NewsQueryKeys.NEWS_RECOMMENDED],
  queryFn: () => getNewsRecommended(),
  localized: true,
  getOptions: () => ({
    refetchOnMount: true,
    meta: { scope: 'news' }
  })
})

export function useNewsRecommended() {
  return useNewsRecommendedFactory({})
}

const useNewsListFactory = createInfiniteHook<
  { limit?: number; firstPageLimit?: number; tag?: string },
  Awaited<ReturnType<typeof getNewsList>>,
  number
>({
  getQueryKey: ({ limit, firstPageLimit, tag }) => [
    NewsQueryKeys.NEWS_LIST,
    limit,
    firstPageLimit ?? limit,
    tag ?? 'all'
  ],
  queryFn: ({ params, pageParam }) =>
    getNewsList({
      offset: pageParam,
      limit:
        pageParam === 0
          ? params.firstPageLimit ?? params.limit ?? DEFAULT_NEWS_LIMIT
          : params.limit ?? DEFAULT_NEWS_LIMIT,
      tag: params.tag
    }),
  initialPageParam: 0,
  getNextPageParam: (lastPage, _allPages, lastPageParam) => {
    const currentOffset = lastPageParam
    const pageLimit = lastPage.limit || DEFAULT_NEWS_LIMIT
    const newsCount = lastPage.news?.length || 0
    const totalCount = lastPage.total || 0

    if (totalCount && currentOffset + pageLimit < totalCount)
      return currentOffset + pageLimit
    if (!totalCount && newsCount === pageLimit) return currentOffset + pageLimit
    return undefined
  },
  localized: true,
  getOptions: () => ({
    refetchOnMount: true,
    meta: { scope: 'news' }
  })
})

export function useNewsList(options?: {
  limit?: number
  firstPageLimit?: number
  tag?: string
}) {
  return useNewsListFactory({
    limit: options?.limit,
    firstPageLimit: options?.firstPageLimit,
    tag: options?.tag
  })
}

const useNewsTagsFactory = createQueryHook<
  Record<string, never>,
  Awaited<ReturnType<typeof getNewsTags>>
>({
  getQueryKey: () => [NewsQueryKeys.NEWS_TAGS],
  queryFn: () => getNewsTags(),
  localized: true,
  getOptions: () => ({
    refetchOnMount: true,
    meta: { scope: 'news' }
  })
})

export function useNewsTags() {
  return useNewsTagsFactory({})
}

const useLatestNewsFactory = createQueryHook<
  { limit?: number; enabled?: boolean },
  Awaited<ReturnType<typeof getNewsList>>
>({
  getQueryKey: ({ limit }) => [NewsQueryKeys.NEWS_LIST, 'latest', limit],
  queryFn: ({ limit }) => getNewsList({ offset: 0, limit }),
  getEnabled: ({ enabled }) => enabled !== false,
  localized: true,
  getOptions: () => ({
    refetchOnMount: true,
    meta: { scope: 'news' }
  })
})

export function useLatestNews(limit?: number, enabled = true) {
  return useLatestNewsFactory({ limit, enabled })
}

const useNewsDetailFactory = createQueryHook<
  { id: string; initialNews?: ModelsNews },
  Awaited<ReturnType<typeof getNewsDetail>>
>({
  getQueryKey: ({ id }) => [NewsQueryKeys.NEWS_DETAIL, id],
  queryFn: ({ id }) => getNewsDetail({ id }),
  getEnabled: ({ id }) => Boolean(id),
  localized: true,
  getOptions: ({ id, initialNews }) => ({
    initialData: initialNews?.id === id ? initialNews : undefined,
    staleTime: 60_000,
    refetchOnMount: true,
    meta: { scope: 'news' }
  })
})

export function useNewsDetail(id: string, initialNews?: ModelsNews) {
  return useNewsDetailFactory({ id, initialNews })
}

const useNewsMainPageFactory = createQueryHook<
  { limit?: number; offset?: number },
  Awaited<ReturnType<typeof getNewsMainPage>>
>({
  getQueryKey: ({ limit, offset }) => [
    NewsQueryKeys.NEWS_MAIN_PAGE,
    limit,
    offset
  ],
  queryFn: ({ limit, offset }) => getNewsMainPage({ limit, offset }),
  localized: true,
  getOptions: () => ({
    refetchOnMount: true,
    meta: { scope: 'news' }
  })
})

export function useNewsMainPage(limit?: number, offset?: number) {
  return useNewsMainPageFactory({ limit, offset })
}

const useNewsFavoriteFactory = createQueryHook<
  Record<string, never>,
  Awaited<ReturnType<typeof getNewsFavorite>>
>({
  getQueryKey: () => [NewsQueryKeys.NEWS_FAVORITE],
  queryFn: () => getNewsFavorite(),
  localized: true,
  getOptions: () => ({
    refetchOnMount: true,
    meta: { scope: 'news' }
  })
})

export function useNewsFavorite() {
  return useNewsFavoriteFactory({})
}

const addNewsLike = async (newsId: string) => {
  await api.v2NewsLikesCreate({ news_id: newsId })
}

const removeNewsLike = async (newsId: string) => {
  await api.v2NewsLikesDelete({ news_id: newsId })
}

export function useNewsLike(newsId: string | undefined) {
  const queryClient = useQueryClient()
  // Localized queries are cached per locale, so the optimistic write has to
  // address the entry the current language actually reads from.
  const locale = useLocale()
  const detailKey = [NewsQueryKeys.NEWS_DETAIL, newsId, locale]

  return useMutation({
    mutationFn: async (isLiked: boolean) => {
      if (!newsId) throw new Error('News ID is required')
      await (isLiked ? removeNewsLike(newsId) : addNewsLike(newsId))
    },
    onMutate: async (isLiked: boolean) => {
      if (!newsId) return

      await queryClient.cancelQueries({ queryKey: detailKey })

      const previousNews = queryClient.getQueryData<ModelsNews>(detailKey)

      if (previousNews) {
        queryClient.setQueryData<ModelsNews>(detailKey, {
          ...previousNews,
          is_liked_by_user: !isLiked,
          likes_count: Math.max(
            0,
            (previousNews.likes_count || 0) + (isLiked ? -1 : 1)
          )
        })
      }

      return { previousNews }
    },
    onError: (_error, _isLiked, context) => {
      if (context?.previousNews && newsId) {
        queryClient.setQueryData(detailKey, context.previousNews)
      }
    },
    onSettled: () => {
      if (newsId) {
        // Prefix match — clears the article in every language it was read in.
        queryClient.invalidateQueries({
          queryKey: [NewsQueryKeys.NEWS_DETAIL, newsId]
        })
      }
    }
  })
}
